import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GameState } from "../../WordDriftUtils";
import * as Lucide from "lucide-react";
import { RootState, playSound, SharedSoundEffect } from '@mental-mint/shared-ui';
import { useSelector } from 'react-redux';

interface TopNavProps {
  /** Current game state for styling and target word detection */
  gameState: GameState;
  /** Color to use for styling */
  gameColor: string;
  /** Callback for when the hint button is clicked */
  onHintClick: () => void;
  /** Whether the hint button should be disabled */
  isHintDisabled?: boolean;
  /** Callback for when help button is clicked */
  onHelpClick: () => void;
  /** Callback for when undo button is clicked */
  onUndo: () => void;
  /** Current hint penalty in seconds */
  currentHintPenalty: number;
  /** Undo penalty in seconds (always 30s) */
  undoPenalty: number;
}

export const TopNav: React.FC<TopNavProps> = ({
  gameState,
  gameColor,
  onHintClick,
  isHintDisabled = false,
  onHelpClick,
  onUndo,
  currentHintPenalty,
  undoPenalty
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;
  
  // State for displaying time penalty in the timer bar
  const [timePenaltySeconds, setTimePenaltySeconds] = useState<number | null>(null);
  
  const capitalize = (s: string | undefined) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  
  // Handle undo button click
  const handleUndo = () => {
    if (gameState.path.length <= 1) {
      return;
    }
    
    // Play same sounds as hint
    playSound(SharedSoundEffect.MENU_CLICK);
    playSound(SharedSoundEffect.HINT);
    
    // Show time penalty indicator in timer bar
    setTimePenaltySeconds(undoPenalty);
    setTimeout(() => {
      setTimePenaltySeconds(null);
    }, 2000);
    
    // Call parent handler to perform undo and time deduction
    onUndo();
  };
  
  const renderBreadcrumb = () => {
    const startWord = gameState.path[0];
    const currentWord = gameState.path[gameState.path.length - 1];
    const goalWord = gameState.goalWord;
    const isOnStartingWord = gameState.path.length === 1;
    
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          gap: "0.25rem",
          padding: "0.75rem 1rem",
          fontFamily: game.fontSecondary,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Only show start word if not on starting word */}
        {!isOnStartingWord && (
          <>
            <span style={{ 
              fontSize: "1rem",
              fontWeight: "400",
              color: "rgba(0, 0, 0, 0.6)",
            }}>
              {capitalize(startWord.variations.lemmatized)}
            </span>
            
            <Lucide.ChevronRight size={16} style={{ color: "rgba(0, 0, 0, 0.4)" }} />
            
            {/* Ellipsis (only show if path is longer than 2) */}
            {gameState.path.length > 2 && (
              <>
                <span style={{
                  color: "rgba(0, 0, 0, 0.5)",
                  fontSize: "0.8rem",
                }}>
                  ...
                </span>
                
                <Lucide.ChevronRight size={16} style={{ color: "rgba(0, 0, 0, 0.4)" }} />
              </>
            )}
          </>
        )}
        
        {/* Current word (larger and colored) */}
        <span style={{ 
          fontSize: "1.2rem",
          fontWeight: "600",
          color: gameColor,
        }}>
          {capitalize(currentWord.variations.lemmatized)}
        </span>
        
        {!gameState.isWon && (
          <>
            <Lucide.ChevronRight size={16} style={{ color: "rgba(0, 0, 0, 0.4)" }} />
            
            <span style={{
              color: "rgba(0, 0, 0, 0.5)",
              fontSize: "0.8rem",
            }}>
              ...
            </span>
            
            <Lucide.ChevronRight size={16} style={{ color: "rgba(0, 0, 0, 0.4)" }} />
            
            {/* Goal word */}
            <span style={{ 
              fontSize: "1rem",
              fontWeight: "400",
              color: "rgba(0, 0, 0, 0.7)",
            }}>
              {capitalize(goalWord.toLowerCase())}
            </span>
          </>
        )}
      </div>
    );
  };
  
  const renderActionButtons = () => {
    const isUndoDisabled = gameState.path.length <= 1 || gameState.isComplete;
    
    // Common button style for icon buttons
    const iconButtonStyle = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "60px",
      height: "40px",
      borderRadius: "6px",
      cursor: "pointer",
      border: "none",
      transition: "all 0.2s ease",
      fontFamily: game.fontSecondary,
      backgroundColor: gameColor,
    };
    
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          padding: "0.25rem 1rem 0.5rem 1rem",
          fontFamily: game.fontSecondary,
        }}
      >
        {/* Undo Button */}
        <button
          onClick={handleUndo}
          disabled={isUndoDisabled}
          style={{
            ...iconButtonStyle,
            opacity: isUndoDisabled ? 0.5 : 1,
            cursor: isUndoDisabled ? "not-allowed" : "pointer",
          }}
          onMouseEnter={(e) => {
            if (!isUndoDisabled) {
              e.currentTarget.style.opacity = "0.85";
            }
          }}
          onMouseLeave={(e) => {
            if (!isUndoDisabled) {
              e.currentTarget.style.opacity = "1";
            }
          }}
          title="Undo last move"
        >
          <Lucide.RotateCcw size={18} strokeWidth={2.5} color="white" />
        </button>
        
        {/* Hint Button */}
        {gameState.optimalPath && (
          <button
            onClick={() => {
              playSound(SharedSoundEffect.MENU_CLICK);
              
              // Show time penalty indicator
              setTimePenaltySeconds(currentHintPenalty);
              setTimeout(() => {
                setTimePenaltySeconds(null);
              }, 2000);
              
              onHintClick();
            }}
            disabled={isHintDisabled}
            style={{
              ...iconButtonStyle,
              opacity: isHintDisabled ? 0.5 : 1,
              cursor: isHintDisabled ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!isHintDisabled) {
                e.currentTarget.style.opacity = "0.85";
              }
            }}
            onMouseLeave={(e) => {
              if (!isHintDisabled) {
                e.currentTarget.style.opacity = "1";
              }
            }}
            title="Get a hint"
          >
            <Lucide.Lightbulb size={18} strokeWidth={2.5} color="white" />
          </button>
        )}
        
        {/* Help Button */}
        <button
          onClick={() => {
            playSound(SharedSoundEffect.MENU_CLICK);
            onHelpClick();
          }}
          style={{
            ...iconButtonStyle,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "0.85";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          title="View instructions and goal word definition"
        >
          <Lucide.HelpCircle size={18} strokeWidth={2.5} color="white" />
        </button>
      </div>
    );
  };

  const renderMovesBar = () => {
    
    const timeRemaining = Math.max(0, gameState.timeLeftSeconds);
    const timeLimit = gameState.timeLimitSeconds;
    const timeElapsed = timeLimit - timeRemaining;
    const progressPercentage = (timeElapsed / timeLimit) * 100;
    
    // Calculate color transition based on time remaining
    const timeUsedRatio = progressPercentage / 100;
    
    // Interpolate between gray and red based on progress
    const getProgressColor = () => {
      // Linear interpolation between gray and red
      const factor = Math.max(0, (timeUsedRatio - 0.5) * 2); // Constant color for first half, 0 to 1 for second half
      
      const initialColorRgb = {r: 100, g: 100, b: 100}; // Gray color for initial
      const lossColorRgb = {r: 220, g: 38, b: 38}; // Red color for loss
      
      const interpolatedR = Math.round(initialColorRgb.r + (lossColorRgb.r - initialColorRgb.r) * factor);
      const interpolatedG = Math.round(initialColorRgb.g + (lossColorRgb.g - initialColorRgb.g) * factor);
      const interpolatedB = Math.round(initialColorRgb.b + (lossColorRgb.b - initialColorRgb.b) * factor);
      
      return `rgb(${interpolatedR}, ${interpolatedG}, ${interpolatedB})`;
    }

    return (
      <motion.div
        className="timer-progress-bar"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "2rem", opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeInOut", delay: 0.3 }}
        style={{
          width: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.08)",
          borderRadius: "0",
          overflow: "hidden",
          position: "relative",
          top: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid rgba(0, 0, 0, 0.15)",
          borderTop: "none",
          boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Progress fill - animate to new value */}
        <motion.div
          animate={{ 
            width: `${progressPercentage}%`,
          }}
          transition={{ 
            width: { duration: 1, ease: "linear" },
          }}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            backgroundColor: getProgressColor(),
            opacity: 0.25,
            borderRadius: "0"
          }}
        />
        
        {/* Time remaining text or penalty display */}
        <AnimatePresence mode="wait">
          {timePenaltySeconds !== null ? (
            <motion.span
              key="penalty"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ 
                scale: [0.5, 1.2, 1],
                opacity: 1
              }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ 
                scale: { duration: 0.4, times: [0, 0.6, 1] },
                opacity: { duration: 0.2 }
              }}
              style={{
                fontSize: "1.1rem",
                fontWeight: "700",
                color: "#DC2626",
                zIndex: 1,
                textShadow: "0 1px 3px rgba(255, 255, 255, 0.9)",
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                fontFamily: game.fontSecondary,
                letterSpacing: "0.02em"
              }}
            >
              <Lucide.Clock size={18} strokeWidth={2.5} />
              -{timePenaltySeconds} sec
            </motion.span>
          ) : (
            <motion.span
              key="timer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                fontSize: "1rem",
                fontWeight: "600",
                color: "#78716c",
                zIndex: 1,
                textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                fontFamily: game.fontSecondary,
                letterSpacing: "0.01em"
              }}
            >
              <Lucide.AlarmClock size={16} strokeWidth={2.2} />
              {(() => {
                const mins = Math.floor(timeRemaining / 60);
                const secs = Math.floor(timeRemaining % 60);
                return `${mins}:${secs.toString().padStart(2, '0')} left`;
              })()}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <>
      <motion.div
          className="top-nav"
          style={{
              display: "flex",
              flexDirection: "column",
              zIndex: 1000,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderBottom: "1px solid #bbbbacff",
              padding: "0",
              margin: "auto",
              maxWidth: "100%",
              left: "50%",
              transform: "translate(-50%, 0)",
              width: "100%",
          }}
          initial={{ 
            top: "0", 
            position: "fixed",
            alignItems: "center",
         }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
      >
          {renderBreadcrumb()}
          {renderActionButtons()}
      </motion.div>
      
      {/* Moves progress bar - also fixed */}
      <div style={{
        position: "fixed",
        top: "0",
        left: "0",
        width: "100%",
        zIndex: 999,
        pointerEvents: "none"
      }}>
        {/* Spacer for the breadcrumb and action buttons height */}
        <div style={{ height: "calc(2.5rem + 3.5rem)" }} />
        <div style={{ pointerEvents: "auto" }}>
          {renderMovesBar()}
        </div>
      </div>
    </>
  );
};
